import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Instagram, Facebook, Star } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { useLang } from "../context/LanguageContext";
import { T } from "../i18n/translations";
import { MEDIA, CONTACT } from "../data/media";

const EASE = [0.16, 1, 0.3, 1];

const Hero = () => {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {MEDIA.heroVideo ? (
          <video
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            poster={MEDIA.heroImage}
            ref={(el) => { if (el) el.play().catch(() => {}); }}
            data-testid="hero-video"
          >
            <source src={MEDIA.heroVideo} type="video/mp4" />
          </video>
        ) : (
          <motion.img
            src={MEDIA.heroImage}
            alt="Trisé Bistrot"
            className="w-full h-full object-cover object-center"
            initial={{ scale: 1.14 }}
            animate={{ scale: 1 }}
            transition={{ duration: 7, ease: "easeOut" }}
            data-testid="hero-image"
          />
        )}
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-trise-blue" />
      <div className="absolute inset-0 bg-gradient-to-t from-trise-blue/70 via-transparent to-trise-blue/30" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col items-center justify-end text-center pb-24 md:pb-28">
        <motion.img
          src={MEDIA.logo}
          alt="Trisé"
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.35, duration: 1.1, ease: EASE }}
          className="w-[44vw] max-w-xs h-auto object-contain mx-auto"
          data-testid="hero-logo"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
          className="mt-8 flex flex-col items-center gap-8"
        >
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/menu"
              data-testid="hero-menu-cta"
              className="group inline-flex items-center gap-2 bg-trise-ivory text-trise-ink px-8 py-4 text-xs uppercase tracking-widen font-bold hover:bg-trise-terra hover:text-trise-ivory transition-colors duration-300"
            >
              {T.hero.ctaMenu[lang]}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/menu#pranzo"
              data-testid="hero-lunch-cta"
              className="group inline-flex items-center gap-2 border border-trise-brass text-trise-ivory px-8 py-4 text-xs uppercase tracking-widen font-bold hover:bg-trise-brass hover:text-trise-blue transition-colors duration-300"
            >
              {T.hero.ctaLunch[lang]}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="flex items-center justify-center gap-5">
            <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="hero-instagram" className="text-trise-ivory/80 hover:text-trise-brass transition-colors">
              <Instagram size={22} />
            </a>
            <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="hero-facebook" className="text-trise-ivory/80 hover:text-trise-brass transition-colors">
              <Facebook size={22} />
            </a>
            <a href={CONTACT.tripadvisor} target="_blank" rel="noopener noreferrer" aria-label="TripAdvisor" data-testid="hero-tripadvisor" className="text-trise-ivory/80 hover:text-trise-brass transition-colors">
              <Star size={22} />
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-2 text-trise-ivory/70 text-xs uppercase tracking-widen"
      >
        {T.hero.scroll[lang]}
        <ArrowDown size={14} className="animate-bounce text-trise-brass" />
      </motion.div>
    </section>
  );
};

const Intro = () => {
  const { lang } = useLang();
  return (
    <section className="bg-trise-ivory py-24 md:py-36" data-testid="intro">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-3">
          <Reveal>
            <span className="text-xs uppercase tracking-widen text-trise-terra">— {T.intro.tag[lang]}</span>
          </Reveal>
        </div>
        <div className="md:col-span-9">
          <Reveal delay={0.05}>
            <p className="text-2xl md:text-4xl font-light leading-[1.3] text-trise-ink">
              {T.intro.body[lang]}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-base md:text-lg text-trise-ink/70 max-w-2xl leading-relaxed">
              {T.intro.body2[lang]}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base md:text-lg text-trise-ink/70 max-w-2xl leading-relaxed">
              {T.intro.body3[lang]}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Pillars = () => {
  const { lang } = useLang();
  return (
    <section className="bg-trise-blue text-trise-ivory py-24 md:py-36 grain relative overflow-hidden" data-testid="pillars">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal delay={0.05}>
            <img src={MEDIA.logo} alt="Trisé" className="h-16 md:h-24 w-auto object-contain" />
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="mt-10 flex items-center gap-2 text-xs uppercase tracking-widen text-trise-brass">
              <span>{T.pillars.story.headingPrefix[lang]}</span>
              <img src={MEDIA.logo} alt="Trisé" className="h-4 md:h-5 w-auto object-contain" />
            </h3>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-6 space-y-6 text-trise-ivory/80 text-base md:text-lg leading-relaxed">
              <p>{T.pillars.story.p1[lang]}</p>
              <p>{T.pillars.story.p2[lang]}</p>
              <p>{T.pillars.story.p3[lang]}</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-14 pt-10 border-t border-trise-brass/25">
              <p className="text-xs uppercase tracking-widen text-trise-brass mb-6">{T.pillars.story.trioHeading[lang]}</p>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-12 gap-y-3">
                {T.pillars.story.trio.map((it, i) => (
                  <p key={i} className="text-2xl md:text-3xl font-bold" data-testid={`pillar-${i}`}>{it[lang]}</p>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-10 text-base md:text-lg text-trise-ivory/80 leading-relaxed">
              {T.pillars.story.closing[lang]}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <h3 className="mt-10 text-3xl md:text-5xl font-bold tracking-tight leading-[1.1] max-w-2xl">
              {T.pillars.story.homeTitle[lang]}
            </h3>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const SpotlightImage = ({ src, alt, className = "" }) => (
  <div className={`relative overflow-hidden ${className}`}>
    <motion.img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover"
      initial={{ scale: 1.12 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.4, ease: EASE }}
    />
  </div>
);

const Gallery = () => {
  const { lang } = useLang();
  return (
    <section className="bg-trise-white py-24 md:py-36" data-testid="gallery">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal>
              <span className="text-xs uppercase tracking-widen text-trise-terra">— {T.gallery.tag[lang]}</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight text-trise-ink max-w-xl">
                {T.gallery.heading[lang]}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/menu"
              data-testid="gallery-menu-cta"
              className="group inline-flex items-center gap-2 text-xs uppercase tracking-widen font-bold text-trise-ink hover:text-trise-terra transition-colors"
            >
              {T.gallery.cta[lang]}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <Reveal className="col-span-12 md:col-span-7" delay={0}>
            <SpotlightImage src={MEDIA.tunaTartare} alt="Tartare di tonno rosso, mango e menta" className="aspect-[4/3] border border-trise-brass/40" />
          </Reveal>
          <Reveal className="col-span-12 md:col-span-5 flex" delay={0.1}>
            <SpotlightImage src={MEDIA.bruschetta} alt="Bruschetta con noci e peperoncino" className="w-full aspect-[3/4] md:aspect-auto border border-trise-brass/40" />
          </Reveal>
          <Reveal className="col-span-12 md:col-span-5 flex" delay={0.05}>
            <SpotlightImage src={MEDIA.social} alt="Esperienza a tavola" className="w-full aspect-[3/4] md:aspect-auto border border-trise-brass/40" />
          </Reveal>
          <Reveal className="col-span-12 md:col-span-7" delay={0.15}>
            <SpotlightImage src={MEDIA.octopus} alt="Polpo laccato su crema di ceci" className="aspect-[4/3] border border-trise-brass/40" />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const HomeLocation = () => {
  const { lang } = useLang();
  return (
    <section className="bg-trise-ivory py-24 md:py-36" data-testid="home-location">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
        <div className="md:col-span-6">
          <Reveal>
            <span className="text-xs uppercase tracking-widen text-trise-terra">— {T.home_location.tag[lang]}</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight text-trise-ink leading-[1.05]">
              {T.home_location.heading[lang]}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg text-trise-ink/70 max-w-xl leading-relaxed">
              {T.home_location.body[lang]}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contatti"
              data-testid="location-cta"
              className="group mt-8 inline-flex items-center gap-2 border border-trise-brass text-trise-ink px-8 py-4 text-xs uppercase tracking-widen font-bold hover:bg-trise-blue hover:text-trise-ivory hover:border-trise-blue transition-colors duration-300"
            >
              {T.home_location.cta[lang]}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
        <div className="md:col-span-6">
          <Reveal delay={0.1}>
            <SpotlightImage src={MEDIA.tartare} alt="Piatto signature Trisé" className="aspect-[5/4] border border-trise-brass/40" />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { lang } = useLang();
  return (
    <>
      <Hero />
      <Intro />
      <Pillars />
      <Marquee text={T.marquee[lang]} />
      <Gallery />
      <HomeLocation />
    </>
  );
}
